import React, { useEffect, useState } from 'react';
import { X, Sparkles, Trophy, Star, ShieldCheck, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TIER_PRIVILEGES, USER_TIERS } from '../../constants';
import { UserTier } from '../../types';
import UserAvatar from '../common/UserAvatar';

interface LevelUpModalProps {
    newTier: UserTier;
    onClose: () => void;
    userPhotoURL?: string | null;
    visitedSpotCount?: number;
}

const LevelUpModal: React.FC<LevelUpModalProps> = ({ newTier, onClose, userPhotoURL, visitedSpotCount = newTier.threshold }) => {
    const { t, i18n } = useTranslation();
    const [show, setShow] = useState(false);
    const tierPrivileges = TIER_PRIVILEGES[newTier.level];
    const nextTier = USER_TIERS.find((tier) => tier.threshold > visitedSpotCount) || null;
    const spotsToNextTier = nextTier ? Math.max(0, nextTier.threshold - visitedSpotCount) : 0;
    const unlockDescription = tierPrivileges
        ? t(`tierGuide.privileges.${newTier.level}`, tierPrivileges.descriptionNo)
        : null;
    const newTierName = t(`tierGuide.tierNames.${newTier.level}`, newTier.name);
    const nextTierName = nextTier ? t(`tierGuide.tierNames.${nextTier.level}`, nextTier.name) : '';

    useEffect(() => {
        // Small delay to trigger entry animations
        const timer = setTimeout(() => setShow(true), 100);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div className={`fixed inset-0 z-[6000] flex items-center justify-center p-4 transition-all duration-500 ${show ? 'opacity-100' : 'opacity-0'}`}>
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/80 backdrop-blur-md"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className={`relative w-full max-w-sm bg-[#151818] border border-[var(--border-secondary)] rounded-modal overflow-hidden shadow-2xl transition-all duration-700 transform ${show ? 'scale-100 translate-y-0' : 'scale-90 translate-y-12'}`} style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>

                {/* Animated Background Glow */}
                <div
                    className="absolute inset-0 opacity-20 blur-[80px] pointer-events-none"
                    style={{ backgroundColor: newTier.color }}
                />

                <div className="relative p-8 flex flex-col items-center text-center space-y-6">
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                    >
                        <X size={20} />
                    </button>

                    {/* Celebration Header */}
                    <div className="space-y-1">
                        <div className="flex justify-center items-center gap-2 text-emerald-400">
                            <Sparkles size={20} className="animate-pulse" />
                            <span className="text-2xs font-black uppercase tracking-[0.2em]">{t('gamification.new_achievement', 'Ny Prestasjon')}</span>
                            <Sparkles size={20} className="animate-pulse" />
                        </div>
                        <h2 className="text-3xl font-black text-[var(--text-primary)] tracking-tight">
                            {t('gamification.level_up', 'Nivå Opp!')}
                        </h2>
                    </div>

                    {/* Avatar with Tier Ring */}
                    <div className="relative pt-4">
                        <div className="absolute inset-0 animate-ping opacity-20 rounded-full" style={{ backgroundColor: newTier.color }} />
                        <UserAvatar
                            photoURL={userPhotoURL}
                            tier={newTier.level}
                            size="xl"
                            className="scale-125 shadow-2xl"
                        />
                        <div
                            className="absolute -bottom-2 -right-2 w-10 h-10 rounded-2xl bg-[#1a1d1d] border border-[var(--border-secondary)] flex items-center justify-center shadow-lg transform rotate-12"
                            style={{ color: newTier.color }}
                        >
                            <Trophy size={20} />
                        </div>
                    </div>

                    {/* Tier Info */}
                    <div className="space-y-3 pt-2">
                        <div className="flex flex-col items-center">
                            <span className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-1">{t('gamification.your_new_level', 'Ditt nye nivå:')}</span>
                            <div
                                className="px-6 py-2 rounded-2xl border-2 font-black text-xl tracking-wider shadow-lg"
                                style={{
                                    borderColor: `${newTier.color}44`,
                                    backgroundColor: `${newTier.color}11`,
                                    color: newTier.color
                                }}
                            >
                                {newTierName.toUpperCase()}
                            </div>
                        </div>

                        <p className="text-[var(--text-secondary)] text-sm leading-relaxed px-4">
                            {t('gamification.unlock_description', 'Du har låst opp {{tier}}-nivået ved å bidra med besøk og verifiseringer i kartet.', { tier: newTierName })}
                        </p>
                    </div>

                    <div className="w-full space-y-3">
                        <div className="w-full rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-left">
                            <div className="flex items-center gap-2 text-2xs font-black uppercase tracking-widest text-emerald-300 mb-2">
                                <ShieldCheck size={14} />
                                <span>{t('gamification.unlocked_now', 'Låst opp nå')}</span>
                            </div>
                            <p className="text-xs font-bold text-emerald-50/90 leading-relaxed">
                                {unlockDescription || 'Ny progresjon og høyere nivåring på profilen din.'}
                            </p>
                        </div>

                        <div className="w-full rounded-3xl border border-white/10 bg-white/5 p-4 text-left">
                            <div className="flex items-center gap-2 text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] mb-2">
                                <Target size={14} />
                                <span>{t('gamification.next_goal', 'Neste mål')}</span>
                            </div>
                            <p className="text-xs font-bold text-[var(--text-primary)] leading-relaxed">
                                {nextTier
                                    ? t('gamification.spots_to_next', '{{count}} plasser igjen til {{tier}} (nivå {{level}}).', { count: spotsToNextTier, tier: nextTierName, level: nextTier.level })
                                    : t('gamification.max_level', 'Du er på høyeste nivå nå. Fortsett å bidra for å holde kartet oppdatert.')}
                            </p>
                        </div>
                    </div>

                    {/* Action Button */}
                    <button
                        onClick={onClose}
                        className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all active:scale-95 shadow-xl hover:brightness-110"
                        style={{
                            backgroundColor: newTier.color,
                            color: '#000'
                        }}
                    >
                        {t('gamification.continue_journey', 'Fortsett Reisen')}
                    </button>

                    {/* Mini Stats/Footer */}
                    <div className="flex items-center gap-4 text-2xs font-black uppercase tracking-widest text-[var(--text-muted)] pt-2">
                        <div className="flex items-center gap-1">
                            <Star size={10} fill="currentColor" />
                            <span>{t('gamification.level', 'Nivå')} {newTier.level}</span>
                        </div>
                        <div className="w-1 h-1 rounded-full bg-stone-700" />
                        <span>{t('gamification.spots_visited', '{{count}} steder besøkt', { count: visitedSpotCount })}</span>
                    </div>
                </div>
            </div>

            {/* Confetti-like particles (CSS Only) */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(12)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute animate-bounce"
                        style={{
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            width: '4px',
                            height: '4px',
                            backgroundColor: newTier.color,
                            opacity: 0.4,
                            animationDelay: `${Math.random() * 2}s`,
                            animationDuration: `${2 + Math.random() * 2}s`
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default LevelUpModal;
